import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { XStack, YStack } from "tamagui";

import { QuestionScrollArea } from "@/conversation/AskUserQuestionsPanel/OptionParts";
import PanelHeader from "@/conversation/AskUserQuestionsPanel/PanelHeader";
import SkipOrSubmit from "@/conversation/AskUserQuestionsPanel/SkipOrSubmit";
import {
  AddressFields,
  type AddressFormData,
  addressSchema,
} from "@/forms/AddressFields";
import { useZipcodeValidation } from "@/forms/hooks/useZipcodeValidation";

const ADDRESS_TITLE = "What's your home address?";

const EMPTY_HOME_ADDRESS: AddressFormData = {
  streetAddress: "",
  streetAddress2: "",
  city: "",
  state: "",
  zipCode: "",
};

const noop = () => {};

export default function HomeAddressCard({
  defaultValues,
  onSubmit,
  isSubmitting = false,
}: {
  defaultValues?: Partial<AddressFormData>;
  onSubmit: (values: AddressFormData) => void;
  isSubmitting?: boolean;
}) {
  const {
    control,
    handleSubmit,
    setValue,
    setFocus,
    formState: { errors },
  } = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    mode: "onBlur",
    defaultValues: { ...EMPTY_HOME_ADDRESS, ...defaultValues },
  });
  const { zipcodeError, isValidatingZipcode } = useZipcodeValidation({
    control,
  });

  const submit = handleSubmit((values) => {
    if (zipcodeError) {
      setFocus("zipCode");
      return;
    }
    if (isValidatingZipcode) return;

    onSubmit(values);
  });

  return (
    <>
      <PanelHeader
        question={ADDRESS_TITLE}
        index={0}
        total={1}
        onPrev={noop}
        onNext={noop}
      />
      <QuestionScrollArea>
        <YStack paddingHorizontal="$150">
          <AddressFields
            control={control}
            errors={errors}
            setValue={setValue}
            zipcodeError={zipcodeError}
          />
        </YStack>
      </QuestionScrollArea>
      <XStack
        justifyContent="flex-end"
        paddingHorizontal="$150"
        marginTop="$250"
        paddingBottom="$150"
      >
        <SkipOrSubmit
          showSkip={false}
          showSubmit
          submitDisabled={isSubmitting || isValidatingZipcode}
          isLast
          onSkip={noop}
          onSubmit={submit}
        />
      </XStack>
    </>
  );
}
